import { PropsWithChildren, useContext, useEffect, useRef, useState } from "react"
import { vec2 } from "gl-matrix"
import { usePointerDrag } from "@/utils/usePointerDrag"
import {
	getImageTransformContext,
	getViewportRect,
	projectImageTransformCoord,
	unprojectPointerCoord,
} from "@/components/canvas-controls/canvas-controls-utils"
import { HealingTransformBoundsContext } from "@/core/utils/healing-transform-bounds"
import {
	isHealingHoverAtom,
	healingItemsAtom,
	healingAiTemporaryItemAtom,
	healingAiVisibleItemAtom,
	removeHealingItem,
	selectedHealingAtom,
	updateHealingItem,
	clearAiHealingCandidate,
} from "../../store/editor/healing-state"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { clamp, rectOffset } from "@/utils/math"
import { HealingEdge } from "../canvas-controls/HealingEdges"
import { BrushStroke, getClampedRects } from "@/core/utils/brushing"
import { Pin } from "../canvas-controls/Pin"
import { HealingItem } from "@/core/types"
import { analytics } from "@/telemetry/amplitude"
import { showContextMenu } from "../ContextMenuManager"
import { HealingContextMenu } from "./HealingContextMenu"
import { CanvasControlsContext } from "../canvas-controls/CanvasControlsContext"
import { cursorAtom } from "@/store/brush-cursor-state"
import {
	healingParametersAdjustingAtom,
	mouseOverSelectedHealingPanelAtom,
	showingHealingPanelAtom,
} from "@/store/ui-views-state"
import { useShortcuts } from "@/utils/useShortcuts"
import { actionShortcuts } from "@/shortcuts/shortcuts"
import { showingMaskingToolsOverlayAtom } from "@/store/editor/masks-state"

const HealingItemControl = (props: {
	healingItem: HealingItem
	canvasRef: React.RefObject<HTMLCanvasElement | null>
	hoverItemId: string | undefined
	isSelected: boolean
	isDragging: boolean
	draggingItem?: HealingItem | null
	hideSource: boolean
	isTemporary?: boolean
	eventPosition: [number, number] | null
	projectionContext: HealingTransformBoundsContext
	handleHealingEdgeEvent: (
		healingItem: HealingItem,
		areaType: "source" | "destination" | null,
	) => void
}) => {
	const [item, setItem] = useState(props.healingItem)

	useEffect(() => {
		setItem(props.healingItem)
	}, [props.healingItem])

	useEffect(() => {
		if (props.isSelected && props.draggingItem) {
			setItem(props.draggingItem)
		}
	}, [props.draggingItem])

	const selectedAiSource = item.aiVariations?.[item.selectedVariation ?? 0]
	const destination =
		item.applyBrushMask === false && selectedAiSource?.fullRect
			? projectImageTransformCoord(
					[
						selectedAiSource.fullRect.x + selectedAiSource.fullRect.width / 2,
						selectedAiSource.fullRect.y + selectedAiSource.fullRect.height / 2,
					],
					props.projectionContext,
				)
			: projectImageTransformCoord(item.destinationCenter, props.projectionContext)
	const showSelecting = props.hoverItemId === item.id || (props.isSelected && !props.hoverItemId)
	// Temporary items always show edges, regular items only when hovering/selected
	const showEdges = props.isTemporary || showSelecting

	return (
		<>
			<HealingEdge
				canvasRef={props.canvasRef}
				healingItem={item}
				hideDestination={props.isDragging}
				hideSource={props.hideSource}
				hidden={!showEdges}
				projectionContext={props.projectionContext}
				eventPosition={props.eventPosition ?? undefined}
				onEventInsideHandler={props.handleHealingEdgeEvent}
			/>
			{/* Hide Pin (central symbol) for temporary items */}
			{!props.isTemporary && destination && (
				<Pin
					name={props.isSelected ? "healing_selected" : "healing"}
					x={destination[0]}
					y={destination[1]}
					size={16}
					opacity={showEdges || props.isSelected ? 1.0 : 0.5}
					data-type="destination"
					className="pointer-events-none"
				/>
			)}
		</>
	)
}

export const HealingControls = (props: PropsWithChildren) => {
	const healingItems = useAtomValue(healingItemsAtom)
	const temporaryItem = useAtomValue(healingAiTemporaryItemAtom) // For deletion check
	const visibleItem = useAtomValue(healingAiVisibleItemAtom) // For rendering (includes pending items)
	const isHealingActive = useAtomValue(showingHealingPanelAtom)
	const [selectedHealing, setSelectedHealing] = useAtom(selectedHealingAtom)
	const setHealingHover = useSetAtom(isHealingHoverAtom)
	const isPointerInsideEditor = useAtomValue(showingMaskingToolsOverlayAtom)
	const mouseOverSelectedHealingPanel = useAtomValue(mouseOverSelectedHealingPanelAtom)
	const isHealingParametersAdjusting = useAtomValue(healingParametersAdjustingAtom)
	const [eventPosition, setEventPosition] = useState<[number, number] | null>(null)
	const [hoverItem, setHoverItem] = useState<HealingItem | null>(null)
	const [draggingItem, setDraggingItem] = useState<HealingItem | null>(null)
	const [isDragging, setDragging] = useState(false)

	const { parentWidth, parentHeight } = useContext(CanvasControlsContext)
	const projectionContext = getImageTransformContext([parentWidth, parentHeight])
	const [eventMap, setEventMap] = useState(new Map<string, "source" | "destination" | null>())
	const [cursor, setCursor] = useAtom(cursorAtom)
	const canvasRef = useRef<HTMLCanvasElement | null>(null)
	const firstRender = useRef(true)
	const previousHealingItemIds = useRef(new Set(healingItems.map((item) => item?.id)))
	useEffect(() => {
		firstRender.current = false
	}, [])

	useEffect(() => {
		const addedHealingItem = healingItems.find(
			(item) => item && !previousHealingItemIds.current.has(item.id),
		)
		previousHealingItemIds.current = new Set(healingItems.map((item) => item?.id))

		if (addedHealingItem) {
			setHoverItem(null)
			setHealingHover(false)
		}
	}, [healingItems, setHealingHover])

	useEffect(() => {
		if (!hoverItem) {
			setHealingHover(false)
		}
	}, [hoverItem, setHealingHover])

	useShortcuts({
		keys: [actionShortcuts.del, actionShortcuts.backspace, actionShortcuts.delete],
		action: () => {
			const itemToDelete = hoverItem || selectedHealing
			if (itemToDelete) {
				eventMap.delete(itemToDelete.id)
				setEventMap(eventMap)

				// Check if it's the temporary item
				if (temporaryItem && itemToDelete.id === temporaryItem.id) {
					clearAiHealingCandidate()
				} else {
					removeHealingItem(itemToDelete)
				}

				setHoverItem(null)
				setHealingHover(false)
			}
		},
		trackingLabel: "delete_healing",
		disabled: !isHealingActive,
	})

	useShortcuts({
		keys: ["left", "right", "up", "down"],
		action: (event) => {
			if (!selectedHealing) {
				return
			}
			if (temporaryItem?.id === selectedHealing.id) {
				return
			}
			let delta = vec2.create()
			switch (event.key) {
				case "ArrowLeft":
					delta[0] = -0.001
					break
				case "ArrowRight":
					delta[0] = 0.001
					break
				case "ArrowUp":
					delta[1] = -0.001
					break
				case "ArrowDown":
					delta[1] = 0.001
					break
			}

			const rect = getViewportRect()
			const pointerStart = unprojectPointerCoord(0, 0)
			const pointer = unprojectPointerCoord(delta[0] * rect.width, delta[1] * rect.height)
			delta = vec2.sub(vec2.create(), pointer, pointerStart)

			const eventState = eventMap.get(selectedHealing.id)
			const prop =
				eventState === "source"
					? getUpdatedHealingSourcePosition(delta)
					: getUpdatedHealingDestinationPosition(delta)

			updateHealingItem(
				selectedHealing.id,
				{ healingProps: prop, changedRegion: eventState ?? undefined },
				true,
			)

			setSelectedHealing({
				...selectedHealing,
				...prop,
			})
		},
		trackingLabel: "move_healing",
		disabled: selectedHealing === null,
	})

	// Clear stale hoverItem when the referenced item no longer exists
	// (e.g. temporary AI item deleted by mode switch while cursor was outside viewport)
	useEffect(() => {
		if (!hoverItem) return
		const existsInItems = healingItems.some((item) => item?.id === hoverItem.id)
		const isVisibleItem = visibleItem?.id === hoverItem.id
		if (!existsInItems && !isVisibleItem) {
			setHoverItem(null)
			setHealingHover(false)
		}
	}, [healingItems, visibleItem, hoverItem])

	const updateHoverItem = () => {
		// Check visible item first (includes temporary draft OR pending generation)
		if (visibleItem) {
			const eventState = eventMap.get(visibleItem.id)
			// For visible items: only set hover when actually over the edges
			// This allows brush cursor to appear when not hovering
			if (eventState === "destination") {
				setHealingHover(true)
				if (hoverItem?.id !== visibleItem.id) {
					setHoverItem(visibleItem)
				}
				return
			}
		}

		// Then check regular healing items
		for (let i = healingItems.length - 1; i >= 0; i--) {
			const item = healingItems[i]
			if (item) {
				const eventState = eventMap.get(item?.id)
				if (eventState) {
					if (selectedHealing?.id === item.id || eventState === "destination") {
						setHealingHover(true)

						if (hoverItem?.id !== item.id) {
							setHoverItem(item)
						}
						return
					}
				}
			}
		}
		// Clear hover if no items are being hovered
		if (hoverItem) {
			setHoverItem(null)
			setHealingHover(false)
		}
	}

	const getUpdatedHealingSourcePosition = (delta: vec2) => {
		if (!selectedHealing) {
			return
		}

		const newSourceCenter = vec2.add(vec2.create(), selectedHealing.sourceCenter, delta)

		const updatedRects = getClampedRects({
			strokesRect: selectedHealing.strokesRect,
			sourceCenter: newSourceCenter,
			destinationCenter: selectedHealing.destinationCenter,
		})

		const prop = {
			sourceCenter: newSourceCenter,
			...updatedRects,
		}
		return prop
	}

	const getUpdatedHealingDestinationPosition = (delta: vec2) => {
		if (!selectedHealing) {
			return
		}
		const newStrokesRect = rectOffset(selectedHealing.strokesRect, delta[0], delta[1])

		const newDestinationCenter = vec2.add(
			vec2.create(),
			selectedHealing.destinationCenter,
			delta,
		)

		const newStrokes = [{ ...selectedHealing.strokes[0] }]
		if (
			newStrokes &&
			newStrokes.length > 0 &&
			newStrokes[0] &&
			selectedHealing.strokes[0]?.points
		) {
			newStrokes[0].points = selectedHealing.strokes[0].points.map(
				(p, index) => p + (index % 2 === 0 ? delta[0] : delta[1]),
			)
		}

		const isAiItem = Boolean(selectedHealing.aiVariations)
		// For AI items, move source with destination to keep rect sizes consistent
		const newSourceCenter = isAiItem
			? vec2.add(vec2.create(), selectedHealing.sourceCenter, delta)
			: selectedHealing.sourceCenter

		const updatedRects = getClampedRects({
			strokesRect: newStrokesRect,
			sourceCenter: newSourceCenter,
			destinationCenter: newDestinationCenter,
		})

		const prop: Partial<HealingItem> = {
			destinationCenter: newDestinationCenter,
			strokes: newStrokes as BrushStroke[],
			strokesRect: newStrokesRect,
			...updatedRects,
		}

		if (isAiItem) {
			prop.sourceCenter = newSourceCenter
			if (selectedHealing.aiVariations) {
				prop.aiVariations = selectedHealing.aiVariations.map((v) => ({
					...v,
					fullRect: rectOffset(v.fullRect, delta[0], delta[1]),
				}))
			}
		}

		return prop
	}

	const handleHealingEdgeEvent = (
		healingItem: HealingItem,
		areaType: "source" | "destination" | null,
	) => {
		if (isDragging) {
			return
		}
		eventMap.set(healingItem.id, areaType)
		setEventMap(eventMap)
		updateHoverItem()
	}

	const handleContextMenu = (e: React.MouseEvent<SVGRectElement>) => {
		// Select the hovered item before showing context menu
		if (hoverItem) {
			setSelectedHealing(hoverItem)
		}

		analytics.trackEvent(
			{
				label: "open_healing_menu",
				type: "click",
				clickType: "right_click",
				value: "healing_menu",
			},
			e.target,
		)
		showContextMenu(e, <HealingContextMenu />)
	}

	const handleEndDragging = () => {
		// update hover item for next dragging
		if (draggingItem && selectedHealing) {
			setHoverItem(draggingItem)
			setSelectedHealing(draggingItem)
			setHealingHover(true)
		}
		if (isDragging) {
			setDragging(false)
			setDraggingItem(null)
		}
	}

	const bindDrag = usePointerDrag<SVGElement>({
		onPointerUp: (e) => {
			e.stopPropagation()
		},
		onPointerDown: (e) => {
			if (!hoverItem) {
				return
			}

			e.stopPropagation()
			setSelectedHealing(hoverItem)

			// Blur any active textarea (like prompt input)
			if (document.activeElement instanceof HTMLTextAreaElement) {
				document.activeElement.blur()
			}

			// update cursor parameters
			const cursorInfo = hoverItem.strokes[0]
			if (cursorInfo) {
				const updatedCursor = {
					...cursor,
					density: cursorInfo.density ?? cursor.density ?? 1.0,
				}
				setCursor(updatedCursor)
			}
		},
		onDrag: (drag) => {
			if (!selectedHealing) {
				return
			}
			if (temporaryItem?.id === selectedHealing.id || temporaryItem?.id === hoverItem?.id) {
				drag.event.stopPropagation()
				return
			}

			// AI items have no source region — only allow destination dragging
			if (selectedHealing.aiVariations && eventMap.get(selectedHealing.id) === "source") {
				return
			}

			drag.event.stopPropagation()
			setDragging(true)

			const pointerStart = unprojectPointerCoord(drag.startX, drag.startY)
			const pointer = unprojectPointerCoord(drag.x, drag.y)
			const delta = vec2.sub(vec2.create(), pointer, pointerStart)

			const eventState = eventMap.get(selectedHealing.id)
			if (eventState === "source") {
				let targetX = selectedHealing.sourceCenter[0] + delta[0]
				let targetY = selectedHealing.sourceCenter[1] + delta[1]
				targetX = clamp(targetX, 0, 1)
				targetY = clamp(targetY, 0, 1)
				delta[0] = targetX - selectedHealing.sourceCenter[0]
				delta[1] = targetY - selectedHealing.sourceCenter[1]

				const prop = getUpdatedHealingSourcePosition(delta)

				updateHealingItem(
					selectedHealing.id,
					{ healingProps: prop, changedRegion: eventState },
					drag.isEnd,
				)
				setDraggingItem({ ...selectedHealing, ...prop })
			} else if (eventState === "destination" || selectedHealing.aiVariations) {
				let targetX = selectedHealing.destinationCenter[0] + delta[0]
				let targetY = selectedHealing.destinationCenter[1] + delta[1]
				targetX = clamp(targetX, 0, 1)
				targetY = clamp(targetY, 0, 1)
				delta[0] = targetX - selectedHealing.destinationCenter[0]
				delta[1] = targetY - selectedHealing.destinationCenter[1]
				const prop = getUpdatedHealingDestinationPosition(delta)

				updateHealingItem(
					selectedHealing.id,
					{ healingProps: prop, changedRegion: "destination" },
					drag.isEnd,
				)
				setDraggingItem({ ...selectedHealing, ...prop })
			}

			if (drag.isEnd) {
				handleEndDragging()
			}
		},
		onPointerMove: (e) => {
			const coords = unprojectPointerCoord(e.pageX, e.pageY)

			setCursor({
				...cursor,
				x: coords[0],
				y: coords[1],
			})

			setEventPosition([e.pageX, e.pageY])
			if (hoverItem) {
				e.stopPropagation()
			}
		},
		onPointerOut: () => {
			setEventPosition(null)
			setHoverItem(null)
			setHealingHover(false)
		},
	})

	const hoveredHealingItem = hoverItem
		? healingItems.find((healingItem) => healingItem?.id === hoverItem.id)
		: undefined
	const healingItemHoverId = hoveredHealingItem?.id

	return (
		<>
			{!firstRender.current && (
				<foreignObject
					x={0}
					y={0}
					width={parentWidth}
					height={parentHeight}
					pointerEvents={"none"}
					{...props}
				>
					<canvas ref={canvasRef} />
				</foreignObject>
			)}
			{healingItems.map((healingItem) => {
				if (!healingItem || !healingItem.sourceRect) {
					return null
				}

				const isSelected = selectedHealing?.id === healingItem.id
				const hideSource =
					healingItemHoverId && healingItemHoverId !== healingItem.id && isSelected

				const isAiItem = Boolean(healingItem.aiVariations)

				if (!isPointerInsideEditor) {
					if (
						!(isSelected && mouseOverSelectedHealingPanel) ||
						isHealingParametersAdjusting
					) {
						return null
					}
				}

				return (
					<HealingItemControl
						key={healingItem.id}
						healingItem={healingItem}
						canvasRef={canvasRef}
						hoverItemId={healingItemHoverId}
						isSelected={isSelected}
						isDragging={isDragging}
						draggingItem={draggingItem}
						hideSource={hideSource === true || isAiItem}
						eventPosition={eventPosition}
						projectionContext={projectionContext}
						handleHealingEdgeEvent={handleHealingEdgeEvent}
					/>
				)
			})}
			{/* Render visible AI item (temporary draft OR pending generation) */}
			{visibleItem && (
				<HealingItemControl
					key={visibleItem.id}
					healingItem={visibleItem}
					canvasRef={canvasRef}
					hoverItemId={hoverItem?.id}
					isSelected={selectedHealing?.id === visibleItem.id}
					isDragging={isDragging}
					draggingItem={draggingItem}
					hideSource={true}
					isTemporary={true}
					eventPosition={eventPosition}
					projectionContext={projectionContext}
					handleHealingEdgeEvent={handleHealingEdgeEvent}
				/>
			)}
			{props.children}
			<rect
				x={0}
				y={0}
				width={parentWidth}
				height={parentHeight}
				strokeWidth={0}
				className={
					hoverItem
						? hoverItem.id === temporaryItem?.id
							? "cursor-default"
							: "cursor-move"
						: ""
				}
				onContextMenu={handleContextMenu}
				{...bindDrag}
			></rect>
		</>
	)
}
